import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getEvent, getEventPDFs } from "../../api/requests";
import { Alert, Card, Button, Spinner, Container } from "react-bootstrap";

const monthNames = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
function getMonthNameFromDateTime(dateTimeStr) {
    const d = new Date(dateTimeStr);
    return monthNames[d.getMonth()];
}
function getDayFromDateTime(dateTimeStr) {
    return new Date(dateTimeStr).getDate();
}
function getYearFromDateTime(dateTimeStr) {
    return new Date(dateTimeStr).getFullYear();
}
function getShortTimeFromDateTime(dateTimeStr) {
    if (!dateTimeStr) return "";
    const d = new Date(dateTimeStr);
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function downloadEventAsICS(event) {
    // .ics-Download-Link generieren (gleiches Format wie in Events.js)
    const dtStart = event?.start_datetime
        ? new Date(event.start_datetime).toISOString().replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z")
        : "";
    const dtEnd = event?.end_datetime
        ? new Date(event.end_datetime).toISOString().replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z")
        : "";
    const ics = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "BEGIN:VEVENT",
        `SUMMARY:${event?.event_name || "Event"}`,
        `DESCRIPTION:${event?.event_description || ""}`,
        event?.start_datetime ? `DTSTART:${dtStart}` : "",
        event?.end_datetime ? `DTEND:${dtEnd}` : "",
        "END:VEVENT",
        "END:VCALENDAR"
    ].filter(Boolean).join("\r\n");

    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${event?.event_name || "event"}.ics`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 100);
}

export default function EventDetail() {
    const { id } = useParams();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pdfs, setPDFs] = useState([]);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setEvent(null);
        setPDFs([]);
        setError("");
        const fetchEvent = async () => {
            try {
                const found = await getEvent(id);
                if (cancelled) return;
                setEvent(found);
                const pdfList = await getEventPDFs(found.id);
                if (!cancelled) setPDFs(pdfList);
            } catch (error) {
                if (!cancelled && error?.response?.status !== 404) {
                    setError("Das Event oder seine Anhänge konnten nicht geladen werden.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchEvent();
        return () => { cancelled = true; };
    }, [id]);

    if (loading) {
        return <div style={{textAlign: "center", marginTop: 40}}><Spinner animation="border" /></div>;
    }
    if (error) return <Container><Alert variant="danger">{error}</Alert></Container>;
    if (!event) {
        return <Container><Card body>Event nicht gefunden.</Card></Container>;
    }

    return (
        <Container>
            <Card>
                <Card.Header>
                    <h2>{event.event_name}</h2>
                </Card.Header>
                <Card.Body>
                    <div style={{ color: "#888", fontSize: 15, marginBottom: 8 }}>
                        {getDayFromDateTime(event.start_datetime)}. {getMonthNameFromDateTime(event.start_datetime)} {getYearFromDateTime(event.start_datetime)} · {getShortTimeFromDateTime(event.start_datetime)} Uhr
                        {event.end_datetime && (
                            <>
                                {" - "}
                                {getDayFromDateTime(event.end_datetime)}. {getMonthNameFromDateTime(event.end_datetime)} {getYearFromDateTime(event.end_datetime)} · {getShortTimeFromDateTime(event.end_datetime)} Uhr
                            </>
                        )}
                    </div>
                    <div style={{ marginBottom: 16 }}>
                        <strong>Beschreibung:</strong>
                        <div className="plain-text">{event.event_description}</div>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                        <strong>Plätze:</strong> {event.number_participants}
                    </div>
                    <div style={{ marginBottom: 16 }}>
                        <strong>Status:</strong> {event.is_active ? "Aktiv" : "Inaktiv"}
                    </div>
                    {pdfs.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <strong>PDFs zum Event:</strong>
                            <ul>
                                {pdfs.map(pdf => (
                                    <li key={pdf.id}>
                                        <a href={pdf.file} target="_blank" rel="noopener noreferrer">
                                            {pdf.file.split('/').pop()}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div style={{ display: "flex", gap: 12 }}>
                        <Button variant="link" style={{ padding: 0 }} onClick={() => downloadEventAsICS(event)}>
                            .ics speichern
                        </Button>
                        <Button as={Link} to="/kalender" variant="secondary">
                            Zurück
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
}
